import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { IoCallOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import {
  requestOneWonVerification,
  confirmOneWonVerification,
  getLatestVerificationRequest,
} from '../../../services/accountVerificationService';

const BANKS = [
  '국민은행', '신한은행', '우리은행', '하나은행', 'NH농협', 'IBK기업', '카카오뱅크', '토스뱅크',
  '케이뱅크', 'SC제일은행', '씨티은행', '대구은행', '부산은행', '광주은행', '전북은행', '경남은행',
  '새마을금고', '우체국', '기타',
];

const AccountVerify = () => {
  const { userData } = useAuth();
  const uid = userData?.uid;
  const [method, setMethod] = useState('upload');
  const [accountType, setAccountType] = useState('personal');
  const [idCardFile, setIdCardFile] = useState(null);
  const [bankFile, setBankFile] = useState(null);
  const [bizLicenseFile, setBizLicenseFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const idCardRef = useRef(null);
  const bankRef = useRef(null);
  const bizLicenseInputRef = useRef(null);
  // 1원 계좌 인증 상태
  const [bank, setBank] = useState(BANKS[0]);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [activeRequest, setActiveRequest] = useState(null); // {id, status, ...}
  const [userCode, setUserCode] = useState('');
  // 인증 완료 시 잠금 (변경 불가)
  const [verifiedInfo, setVerifiedInfo] = useState(null); // {accountType, bank_name, account_number, account_holder}

  useEffect(() => {
    if (!uid) return;
    getLatestVerificationRequest(uid).then((r) => {
      if (r && (r.status === 'requested' || r.status === 'pending')) {
        setActiveRequest(r);
        setBank(r.bank_name || BANKS[0]);
        setAccountNumber(r.account_number || '');
        setAccountHolder(r.account_holder || '');
      }
    }).catch(() => {});
    getDoc(doc(db, 'realtors', uid)).then((snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.accountVerified === true || d.account_verified === true) {
        setVerifiedInfo({
          accountType: d.account_type || d.accountType || '',
          bank_name: d.bank_name || '',
          account_number: d.account_number || '',
          account_holder: d.account_holder || '',
        });
      }
    }).catch(() => {});
  }, [uid]);

  // 인증 완료 상태 — 잠금 화면
  if (verifiedInfo) {
    return (
      <Container>
        <VerifiedCard>
          <VerifiedIcon><IoCheckmarkCircle size={48} /></VerifiedIcon>
          <VerifiedTitle>계좌 인증이 완료되었습니다</VerifiedTitle>
          <VerifiedDesc>
            인증된 계좌 정보는 변경할 수 없습니다.<br />
            정보 변경이 필요하시면 고객센터로 문의해 주세요.
          </VerifiedDesc>
          <VerifiedInfoList>
            <VerifiedRow><b>계좌 유형</b> {verifiedInfo.accountType === 'business' ? '사업자' : '개인'}</VerifiedRow>
            {verifiedInfo.bank_name && <VerifiedRow><b>은행</b> {verifiedInfo.bank_name}</VerifiedRow>}
            {verifiedInfo.account_number && (
              <VerifiedRow>
                <b>계좌번호</b> {verifiedInfo.account_number.replace(/(\d{4})(?=\d)/g, '$1-')}
              </VerifiedRow>
            )}
            {verifiedInfo.account_holder && <VerifiedRow><b>예금주</b> {verifiedInfo.account_holder}</VerifiedRow>}
          </VerifiedInfoList>
          <PhoneLink onClick={() => window.location.href = 'tel:070-7666-9413'}>
            <IoCallOutline size={14} /> 고객센터 문의 ( 070-7666-9413 )
          </PhoneLink>
        </VerifiedCard>
      </Container>
    );
  }

  return (
    <Container>
      {/* 상단 안내 영역 */}
      <InfoCard>
        <CardIcon>💳</CardIcon>
        <InfoTitle>계좌 인증이 필요합니다.</InfoTitle>
        <InfoDesc>
          출금 신청을 하려면 신분증·통장사본(및 사업자등록증)을 제출하거나, 1원 계좌 인증을 진행해 주세요.
        </InfoDesc>
        <InfoTax>
          세금: 개인 원천징수 3.3%,{'\n'}
          부가세 포함 금액이며{'\n'}
          개인의 경우 부가세 10% 공제 후 지급
        </InfoTax>
        <PhoneLink onClick={() => window.location.href = 'tel:070-7666-9413'}>
          <IoCallOutline size={14} /> 고객센터 문의 ( 070-7666-9413 )
        </PhoneLink>
      </InfoCard>

      {/* 인증방법 선택 */}
      <SectionLabel>인증방법</SectionLabel>
      <BtnRow>
        <ChoiceBtn $active={method === 'upload'} onClick={() => setMethod('upload')}>서류 업로드</ChoiceBtn>
        <ChoiceBtn $active={method === 'verify'} onClick={() => setMethod('verify')}>1원 계좌 인증</ChoiceBtn>
      </BtnRow>

      {/* 계좌유형 선택 (서류 업로드에만 노출, 1원 인증 시 숨김) */}
      {method === 'upload' && (
        <>
          <SectionLabel>계좌유형</SectionLabel>
          <BtnRow>
            <ChoiceBtn $active={accountType === 'personal'} onClick={() => setAccountType('personal')}>개인</ChoiceBtn>
            <ChoiceBtn $active={accountType === 'business'} onClick={() => setAccountType('business')}>사업자</ChoiceBtn>
          </BtnRow>
        </>
      )}

      {method === 'upload' && (
        <>
          {/* 신분증 */}
          <SectionLabel>신분증</SectionLabel>
          <input type="file" accept="image/*" ref={idCardRef} hidden onChange={(e) => setIdCardFile(e.target.files[0])} />
          <UploadBox onClick={() => idCardRef.current?.click()}>
            <PlusIcon>+</PlusIcon>
            <UploadText>{idCardFile ? idCardFile.name : '신분증 사진 추가'}</UploadText>
          </UploadBox>

          {/* 통장사본 */}
          <SectionLabel>통장사본</SectionLabel>
          <input type="file" accept="image/*" ref={bankRef} hidden onChange={(e) => setBankFile(e.target.files[0])} />
          <UploadBox onClick={() => bankRef.current?.click()}>
            <PlusIcon>+</PlusIcon>
            <UploadText>{bankFile ? bankFile.name : '통장사본 사진 추가'}</UploadText>
          </UploadBox>

          {/* 사업자등록증 (사업자만) */}
          {accountType === 'business' && (
            <>
              <SectionLabel>사업자등록증</SectionLabel>
              <input type="file" accept="image/*,application/pdf" ref={bizLicenseInputRef} hidden onChange={(e) => setBizLicenseFile(e.target.files[0])} />
              <UploadBox onClick={() => bizLicenseInputRef.current?.click()}>
                <PlusIcon>+</PlusIcon>
                <UploadText>{bizLicenseFile ? bizLicenseFile.name : '사업자등록증 파일 추가'}</UploadText>
              </UploadBox>
            </>
          )}
        </>
      )}

      {method === 'verify' && !activeRequest && (
        <>
          <SectionLabel>은행 선택</SectionLabel>
          <Select value={bank} onChange={(e) => setBank(e.target.value)}>
            {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
          </Select>

          <SectionLabel>계좌번호</SectionLabel>
          <Input
            placeholder="- 없이 숫자만 입력"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
          />

          <SectionLabel>예금주</SectionLabel>
          <Input
            placeholder="예금주명"
            value={accountHolder}
            onChange={(e) => setAccountHolder(e.target.value)}
          />
        </>
      )}

      {method === 'verify' && activeRequest && (
        <>
          <PendingBox>
            <PendingTitle>입금자명 4자리 숫자를 확인해주세요</PendingTitle>
            <PendingDesc>
              아래 계좌로 이음에서 <b>1원</b>을 송금해 드립니다.<br />
              입금 내역의 <b>입금자명에 찍힌 4자리 숫자</b>를 아래 칸에 입력해주세요.<br />
              (최대 24시간 이내 도착 / 안 올 경우 고객센터 문의)
            </PendingDesc>
            <PendingInfo>
              <div><b>은행</b> {activeRequest.bank_name}</div>
              <div><b>계좌</b> {activeRequest.account_number}</div>
              <div><b>예금주</b> {activeRequest.account_holder}</div>
            </PendingInfo>
          </PendingBox>

          <SectionLabel>4자리 인증 코드</SectionLabel>
          <Input
            placeholder="0000"
            value={userCode}
            onChange={(e) => setUserCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputMode="numeric"
            maxLength={4}
          />
        </>
      )}

      {/* 하단 버튼 */}
      <PrimaryBtn
        disabled={submitting}
        onClick={async () => {
          if (method === 'upload') {
            if (!uid) { alert('로그인이 필요합니다.'); return; }
            if (!idCardFile || !bankFile) { alert('신분증과 통장사본을 모두 첨부해주세요.'); return; }
            if (accountType === 'business' && !bizLicenseFile) {
              alert('사업자등록증을 첨부해주세요.'); return;
            }
            setSubmitting(true);
            try {
              const idSnap = await uploadBytes(ref(storage, `verifications/${uid}/idCard`), idCardFile);
              const idCardUrl = await getDownloadURL(idSnap.ref);
              const bankSnap = await uploadBytes(ref(storage, `verifications/${uid}/bankStatement`), bankFile);
              const bankStatementUrl = await getDownloadURL(bankSnap.ref);
              const patch = { idCardUrl, bankStatementUrl, accountType, accountVerified: false };
              if (accountType === 'business' && bizLicenseFile) {
                const ext = bizLicenseFile.name.split('.').pop();
                const bizSnap = await uploadBytes(ref(storage, `verifications/${uid}/bizLicense.${ext}`), bizLicenseFile);
                patch.bizLicenseUrl = await getDownloadURL(bizSnap.ref);
              }
              await updateDoc(doc(db, 'realtors', uid), patch);
              alert('서류가 제출되었습니다. 관리자 확인 후 인증이 완료됩니다.');
            } catch (err) {
              alert(err.message || '업로드 실패');
            } finally {
              setSubmitting(false);
            }
          } else {
            // 1원 계좌 인증 — 요청 OR 코드 확인
            if (!uid) { alert('로그인이 필요합니다.'); return; }
            setSubmitting(true);
            try {
              if (!activeRequest) {
                if (!accountNumber || !accountHolder) {
                  alert('계좌번호와 예금주를 입력해주세요.');
                  setSubmitting(false); return;
                }
                const res = await requestOneWonVerification(uid, {
                  bankName: bank,
                  accountNumber,
                  accountHolder,
                  accountType,
                });
                setActiveRequest({
                  id: res.id,
                  status: 'requested',
                  bank_name: bank,
                  account_number: accountNumber,
                  account_holder: accountHolder,
                });
                alert('인증 요청이 접수됐습니다.\n1원이 입금되면 입금자명 4자리 숫자를 확인 후 입력해주세요.');
              } else {
                if (!userCode || userCode.length !== 4) {
                  alert('4자리 숫자를 입력해주세요.');
                  setSubmitting(false); return;
                }
                await confirmOneWonVerification(uid, activeRequest.id, userCode);
                alert('계좌 인증이 완료됐습니다.');
                setActiveRequest(null);
                setUserCode('');
              }
            } catch (err) {
              alert(err.message || '요청 실패');
            } finally {
              setSubmitting(false);
            }
          }
        }}
      >
        {submitting ? '처리 중...' : method === 'upload' ? '서류 제출하기' : (activeRequest ? '인증 코드 확인' : '1원 인증 요청')}
      </PrimaryBtn>
    </Container>
  );
};

export default AccountVerify;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const VerifiedCard = styled.div`
  background: #ECFDF5;
  border: 1px solid #A7F3D0;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const VerifiedIcon = styled.div`
  color: #10B981;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const VerifiedTitle = styled.h2`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #065F46;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const VerifiedDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #047857;
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const VerifiedInfoList = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  text-align: left;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const VerifiedRow = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  padding: 6px 0;

  b {
    display: inline-block;
    width: 72px;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
  }
`;

const InfoCard = styled.div`
  background: #F0F5FF;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CardIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const InfoTitle = styled.h2`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const InfoDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const InfoTax = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.5;
  white-space: pre-line;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PhoneLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
`;

const SectionLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.sm};
`;

const BtnRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ChoiceBtn = styled.button`
  flex: 1;
  height: 44px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $active }) => $active ? '#F0F5FF' : '#fff'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
`;

const UploadBox = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgWhite};
  cursor: pointer;
`;

const PlusIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.full};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.colors.textLight};
`;

const UploadText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.textLight};
`;

const Select = styled.select`
  width: 100%;
  height: 48px;
  padding: 0 ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  background: ${({ theme }) => theme.colors.bgWhite};
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PendingBox = styled.div`
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const PendingTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #92400E;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const PendingDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #78350F;
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const PendingInfo = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  height: 48px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  margin-top: ${({ theme }) => theme.spacing.xl};
`;
